import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { PolicyApplication, ResidentialService } from '../../core/services/residential.service';
import { BrandingService } from '../../core/services/branding.service';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { AppService } from '../../core/services/app.service';
import { GaEcommerceService } from '../../core/services/ga-ecommerce.service';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { DisclosuresPageComponent } from '../../shared/disclosures-page/disclosures-page.component';
import { environment } from '@environment';
import { DataLayerResidentialService } from '../../core/services/data-layer-residential.service';
import { DisclosuresQbeComponent } from '../../shared/disclosures-qbe/disclosures-qbe.component';
import * as moment from 'moment';
import { IframeModeService } from '../../core/services/iframe-mode.service';

@Component({
  selector: 'app-residential-payment',
  templateUrl: './residential-payment.component.html',
  styleUrls: ['./residential-payment.component.scss']
})
export class ResidentialPaymentComponent implements OnDestroy {
  @Input() data: PolicyApplication;
  @Input() residentialForm;
  @Input() selectedPaymentPlan;
  @Input() paymentPlans;
  @Input() addressData;
  @Input() hasBackButton = false;
  @Input() loading;
  @Output() paymentSuccess = new EventEmitter();
  @Output() paymentPlanChange = new EventEmitter();
  @Output() paymentError = new EventEmitter();
  @Output() declinePayment = new EventEmitter();
  @Output() cardComplete = new EventEmitter();
  newCarrierAgree = false;
  environment = environment;
  loader = false;
  result;
  policyDetails;
  subjectFormSubmit = new Subject();
  customRmPage$;

  constructor(private residentialService: ResidentialService,
              private brandingService: BrandingService,
              private dialog: MatDialog,
              private toastrService: ToastrService,
              private appService: AppService,
              private gaEcommerceService: GaEcommerceService,
              private dataLayerResidentialService: DataLayerResidentialService,
              private translate: TranslateService,
              private IFrameModeService: IframeModeService) {
    this.customRmPage$ = this.brandingService.customRgPage$;
  }

  processPayment(value) {
    if (typeof value.error !== 'undefined' && value.error !== false) {
        this.toastrService.error(this.translate.instant('msi_payment.card_declined'));
        this.loader = false;
        return;
    }

    this.loader = true;
    const body: any = {
      user: {
        id: this.data.user.id,
        payment_method: value.id,
      }
    };

    if (this.data.carrier_id === 5) {
      const card = value.card;
      body.user.source = 'tok_visa';
      body.payment = {
        payment_method: 'card',
        token: card.token,
        CreditCardInfo: {
          CardHolderName: card.name,
          CardExpirationDate: `${card.exp.month}${card.exp.year % 100}`,
          CardType: card.brand === 'american-express' ? 'AMEX' : card.brand,
          CreditCardLast4Digits: card.last4,
          Addr: {
            Addr1: card.address1,
            Addr2: card.address2,
            City: card.city,
            StateProvCd: card.region,
            PostalCode: card.postalCode
          }
        }
      };
    }

    const policyTypeId = 1;
    const allInvoicesPrice = this.data.invoices.reduce((acc, val) => acc + val.total_payable, 0);
    const price = this.gaEcommerceService.formatPrice(allInvoicesPrice);

    this.residentialService.acceptPolicyQuote(this.data.quote.id, body).pipe(
      finalize(() => this.loader = false)
    ).subscribe((result: any) => {
        const obj =  {
          transaction_id: this.data.quote.id,
          value: price,
          currency: 'USD',
          items: [this.gaEcommerceService.getProduct(policyTypeId, {
            price
          })],
        };

        window.gtag('event', 'purchase', obj);
        this.dataLayerResidentialService.formSubmitTwo();

        this.result = result;
        this.policyDetails = this.generatePolicyDetails(result.policy_number);
        this.paymentSuccess.emit(result);
        this.IFrameModeService.sendMessage({
          event_type: 'purchase'
        });
      },
      error => {
        const msg = error && error.message ? error.message : error && error.error ? error.error : 'Error!';
        this.paymentError.emit(error);
        this.toastrService.error(msg);
      });
  }

  decline() {
    this.declinePayment.emit();
  }

  goToDisclosure() {
    this.newCarrierAgree = null;
    const state: any = {
      monthlyDownPayment: this.data?.invoices[0]?.total_payable,
      state: this.addressData.state,
      email: this.addressData.email,
      billingTitle: this.selectedPaymentPlan.title,
      fee : this.data.installment_fee,
      installmentTotal: this.data.installment_total,
      policyFee: this.data.policy_fee
    };

    const dialog = this.dialog.open(DisclosuresPageComponent, {
      panelClass: 'disclosures',
      data: {
        pageData: state
      }
    });
    dialog.afterClosed().subscribe((res) => {
      this.newCarrierAgree = res;
      if (res) {
        this.subjectFormSubmit.next();
      }
    });
  }

  goToDisclosureQBE(value) {
    const state = {
      initialCharge: this.data?.invoices[0]?.total_payable,
      monthlyCharge: this.data?.invoices[1]?.total_payable
    };

    const dialog = this.dialog.open(DisclosuresQbeComponent, {
      panelClass: 'disclosures',
      data: {
        pageData: state
      }
    });
    dialog.afterClosed().subscribe((res) => {
      if (res) {
        this.processPayment(value);
      } else {
        this.loader = false;
      }
    });
  }

  onPaymentPlanChanged(value) {
    this.paymentPlanChange.emit(value);
  }

  ngOnDestroy(): void {
    this.appService.setShowMenu(true);
  }

  get isQBE() {
    return this.residentialForm && this.residentialForm.carrier_id && this.residentialForm.carrier_id === 1;
  }

  private generatePolicyDetails(policyNumber) {
    const sDate = this.residentialForm?.effective_date ? moment(this.residentialForm.effective_date).format('MM/DD/YYYY') : 'N/A';
    const eDate = this.residentialForm?.expiration_date ? moment(this.residentialForm.expiration_date).format('MM/DD/YYYY') : 'N/A';
    return {
      policyNumber,
      address: this.addressData,
      unit: this.residentialForm?.policy_users_attributes[0]?.user_attributes?.address_attributes?.street_two,
      insuredNames: this.residentialForm.policy_users_attributes.map(item => {
        return item?.user_attributes?.profile_attributes?.first_name + ' ' + item?.user_attributes?.profile_attributes?.last_name
      }),
      effectiveDates: sDate + ' - ' + eDate,
      paymentPlan: this.selectedPaymentPlan ? this.selectedPaymentPlan.title : ''
    }
  }
}
